import { ODONTO_PATHS, type OdontoFacePart } from './odontograma-paths.generated'

/** Estado persistido do odontograma. Chave = fdi (dente inteiro, ex. "16") ou `${fdi}:${M|D|V|L|P|O}`
 * (uma região da face, ex. "16:M"). Chave ausente = não marcado.
 *
 * O VALOR tem dois significados diferentes dependendo da chave:
 * - Chave de face (`fdi:surface`): o `label` do estágio atual (de `stages`, ex. "Pendente") —
 *   guardamos o rótulo, não o índice, pra dados já salvos não virarem o estágio errado se a ordem
 *   de `stages` mudar depois.
 * - Chave de dente inteiro (`fdi`): só a PRESENÇA da chave importa (dente selecionado
 *   manualmente, ver `TOOTH_SELECTED_MARKER`) — o dente não tem mais estágio próprio, só
 *   selecionado/não selecionado; a cor exibida vem das faces (ver `toothDisplayColor`). */
export type OdontogramaValue = Record<string, string>

export function createDefaultOdontogramaValue(): OdontogramaValue {
    return {}
}

export function isOdontogramaValueEmpty(value: OdontogramaValue | null | undefined): boolean {
    return !value || Object.keys(value).length === 0
}

/** Um estágio de tratamento configurável (rótulo + cor do ciclo de clique). */
export interface OdontoStage {
    label: string
    color: string
}

/** Avança `key` (fdi inteiro ou `${fdi}:${surface}`) pro próximo estágio do ciclo: indefinido ->
 * stages[0] -> stages[1] -> ... -> stages[last] -> indefinido de novo. Função pura — usada tanto
 * pelo odontograma gráfico (clique no SVG) quanto pela lista de dentes (checkbox), já que os dois
 * component compartilham o mesmo `value` e precisam ciclar exatamente da mesma forma. */
export function cycleStageValue(value: OdontogramaValue, stages: OdontoStage[], key: string): OdontogramaValue {
    const currentLabel = value[key]
    const currentIdx = currentLabel != null ? stages.findIndex((s) => s.label === currentLabel) : -1
    const nextIdx = currentIdx + 1
    if (nextIdx >= stages.length) {
        const { [key]: _removed, ...rest } = value
        return rest
    }
    return { ...value, [key]: stages[nextIdx].label }
}

/** Marcador do dente inteiro selecionado manualmente — não é um rótulo de estágio (o dente não
 * cicla mais estágio próprio), só sinaliza presença. Qualquer string funcionaria; esse valor é só
 * pra ficar legível ao inspecionar o JSON salvo. */
export const TOOTH_SELECTED_MARKER = 'selecionado'

/** Cinza usado quando o dente está selecionado mas nenhuma face tem estágio — mesmo cinza de fundo
 * da tag/pill da lista de dentes (ver OdontogramaInputs.svelte `.tag-pill`). */
export const TOOTH_NO_FACE_COLOR = '#e9ecef'

/** As 6 superfícies de face possíveis — usado pra filtrar chaves de face (`fdi:M`, `fdi:D`, ...)
 * separando-as de outras chaves com o mesmo prefixo `fdi:` que não são face (ex.: `fdi:nota`). */
const FACE_SURFACES: ToothSurface[] = ['M', 'D', 'V', 'L', 'P', 'O']

/** Sufixo da chave de anotação (`${fdi}:nota`) — nível de dente, não de face. */
const NOTE_SUFFIX = 'nota'

export function getToothNote(value: OdontogramaValue, fdi: string): string {
    return value[`${fdi}:${NOTE_SUFFIX}`] ?? ''
}

/** Grava a anotação do dente; texto vazio remove a chave (mesma convenção do resto do value:
 * ausente = sem dado, em vez de guardar string vazia). */
export function setToothNote(value: OdontogramaValue, fdi: string, note: string): OdontogramaValue {
    const key = `${fdi}:${NOTE_SUFFIX}`
    if (!note.trim()) {
        const { [key]: _removed, ...rest } = value
        return rest
    }
    return { ...value, [key]: note }
}

export interface ToothNoteEntry {
    fdi: string
    note: string
}

/** Dentes com anotação (não vazia), em ordem clínica ascendente — usado na impressão pra listar
 * todas as anotações de uma vez (o odontograma de input, onde a anotação é editada, não aparece
 * impresso). */
export function teethWithNotes(value: OdontogramaValue): ToothNoteEntry[] {
    const result: ToothNoteEntry[] = []
    for (const fdi of taggedTeeth(value)) {
        const note = getToothNote(value, fdi)
        if (note.trim()) result.push({ fdi, note })
    }
    return result
}

/** Seleciona o dente inteiro (usado ao adicionar pela busca da lista de dentes) — nasce sem
 * nenhuma face marcada, então aparece cinza (ver `toothDisplayColor`) até o usuário marcar uma
 * face de verdade. */
export function addToothValue(value: OdontogramaValue, fdi: string): OdontogramaValue {
    return { ...value, [fdi]: TOOTH_SELECTED_MARKER }
}

/** Liga/desliga só o marcador manual do dente inteiro — nunca mexe nas faces. Selecionado(sem
 * faces) -> não selecionado, ou vice-versa. Se o dente já está selecionado por ter alguma face
 * marcada (ver `toothDisplayColor`), ligar o marcador aqui não muda nada visível ainda, mas
 * garante que ele continue selecionado (cinza) se as faces forem desmarcadas depois. */
export function toggleToothSelected(value: OdontogramaValue, fdi: string): OdontogramaValue {
    if (value[fdi] != null) {
        const { [fdi]: _removed, ...rest } = value
        return rest
    }
    return { ...value, [fdi]: TOOTH_SELECTED_MARKER }
}

/** Cor do dente inteiro entre as faces marcadas: a do estágio de MENOR índice em `stages` dentre
 * os usados em alguma face (índice menor = prioridade maior) — `null` se nenhuma face tem
 * estágio. */
export function toothFaceColor(value: OdontogramaValue, stages: OdontoStage[], fdi: string): string | null {
    let bestIdx = -1
    for (const surface of markedSurfacesForTooth(value, fdi)) {
        const label = value[`${fdi}:${surface}`]
        const idx = stages.findIndex((s) => s.label === label)
        if (idx !== -1 && (bestIdx === -1 || idx < bestIdx)) bestIdx = idx
    }
    return bestIdx === -1 ? null : stages[bestIdx].color
}

/** Cor exibida do dente inteiro (gráfico e lista de dentes usam a mesma regra): a cor da face de
 * maior prioridade se houver alguma marcada; senão, cinza se o dente estiver selecionado
 * manualmente (marcador presente, sem faces); senão `null` (não selecionado, sem preenchimento). */
export function toothDisplayColor(value: OdontogramaValue, stages: OdontoStage[], fdi: string): string | null {
    const faceColor = toothFaceColor(value, stages, fdi)
    if (faceColor != null) return faceColor
    return value[fdi] != null ? TOOTH_NO_FACE_COLOR : null
}

/** Remove TODAS as marcações de um dente (o dente inteiro + qualquer face + anotação) — usado ao
 * remover a tag/pill da lista de dentes, que precisa fazer o dente sumir de vez da lista (a lista
 * é derivada de `value`, ver `taggedTeeth`). */
export function removeToothValue(value: OdontogramaValue, fdi: string): OdontogramaValue {
    const next = { ...value }
    delete next[fdi]
    for (const surface of FACE_SURFACES) {
        delete next[`${fdi}:${surface}`]
    }
    delete next[`${fdi}:${NOTE_SUFFIX}`]
    return next
}

/** Fdis com alguma marcação (dente inteiro OU qualquer face) — a lista de tags na visão "lista de
 * dentes" é 100% derivada de `value`, sem estado próprio: adicionar/remover uma marcação já
 * adiciona/remove a tag correspondente. Ordenação numérica ascendente cai naturalmente na ordem
 * clínica (11..18, 21..28, 31..38, 41..48, 51..85). */
export function taggedTeeth(value: OdontogramaValue): string[] {
    const set = new Set<string>()
    for (const key of Object.keys(value)) {
        set.add(key.split(':')[0])
    }
    return [...set].sort((a, b) => Number(a) - Number(b))
}

/** Faces marcadas de um dente específico — usado só pro resumo compacto na tag (ex.: "11 (M, D)").
 * Filtra só sufixos que são realmente uma face (exclui `:nota`, que tem o mesmo prefixo `fdi:` mas
 * não é uma superfície). */
export function markedSurfacesForTooth(value: OdontogramaValue, fdi: string): ToothSurface[] {
    const prefix = `${fdi}:`
    return Object.keys(value)
        .filter((key) => key.startsWith(prefix) && (FACE_SURFACES as string[]).includes(key.slice(prefix.length)))
        .map((key) => key.slice(prefix.length) as ToothSurface)
}

/** Quadrantes 1/2/5/6 são superiores, 3/4/7/8 são inferiores (FDI) — usado pra resolver
 * automaticamente qual dos dois (L ou P) o toggle "L/P" da lista de dentes deve gravar. */
export function archForFdi(fdi: string): 'upper' | 'lower' {
    const quadrant = fdi[0]
    return quadrant === '1' || quadrant === '2' || quadrant === '5' || quadrant === '6' ? 'upper' : 'lower'
}

// Todos os 62 códigos FDI válidos, em ordem clínica ascendente — usado como universo de busca na
// lista de dentes (esse componente não tem abas Permanentes/Decíduos, é uma lista única).
const PERMANENT_FDI = [
    '11', '12', '13', '14', '15', '16', '17', '18',
    '21', '22', '23', '24', '25', '26', '27', '28',
    '31', '32', '33', '34', '35', '36', '37', '38',
    '41', '42', '43', '44', '45', '46', '47', '48',
]
const DECIDUOUS_FDI = [
    '51', '52', '53', '54', '55',
    '61', '62', '63', '64', '65',
    '71', '72', '73', '74', '75',
    '81', '82', '83', '84', '85',
]
export const ALL_TOOTH_FDI: string[] = [...PERMANENT_FDI, ...DECIDUOUS_FDI]

// FDI: quadrantes 1-4 são permanentes, 5-8 são decíduos (ex.: "51", "63:M"). Usado pra decidir se
// a seção de decíduos aparece na impressão (só aparece se houver algo marcado nela).
const DECIDUOUS_QUADRANTS = new Set(['5', '6', '7', '8'])

export function hasDeciduousValue(value: OdontogramaValue | null | undefined): boolean {
    if (!value) return false
    return Object.keys(value).some((key) => DECIDUOUS_QUADRANTS.has(key[0]))
}

/** Superfícies dentárias na nomenclatura odontológica padrão (FDI):
 * M=Mesial, D=Distal, V=Vestibular, L=Lingual, P=Palatal, O=Oclusal.
 * (Incisal/"I" não está aqui — as faces anteriores têm só 4 regiões, sem centro; ver
 * `surfaceFor` abaixo.) */
export type ToothSurface = 'M' | 'D' | 'V' | 'L' | 'P' | 'O'

/** Rótulo geométrico (ver split-subpaths.mjs) -> superfície odontológica.
 *
 * `esquerda`/`direita` são fixos (D/M) INDEPENDENTE do dente estar espelhado ou não: mesial é
 * sempre "voltada pra linha média" e distal "voltada pra fora" — espelhar o desenho pro lado
 * oposto da arcada é anatomicamente equivalente a virar o mesmo dente pro outro lado da boca, e
 * isso preserva qual superfície é qual (só muda de que lado da tela ela some/aparece).
 *
 * `topo` depende da arcada: Palatal nos superiores, Lingual nos inferiores. `base` (vestibular)
 * e `centro` (oclusal, só em posteriores) são fixos. */
export function surfaceFor(label: OdontoFacePart['label'], arch: 'upper' | 'lower'): ToothSurface {
    switch (label) {
        case 'esquerda':
            return 'D'
        case 'direita':
            return 'M'
        case 'base':
            return 'V'
        case 'centro':
            return 'O'
        case 'topo':
            return arch === 'upper' ? 'P' : 'L'
    }
}

/** Índice (no array ODONTO_PATHS) de cada peça gráfica que compõe uma posição dentária. */
export interface ToothAssetRef {
    /** Contorno principal do dente (coroa + raiz). */
    tooth: number
    /** Traço decorativo do "colo" do dente (canal/bifurcação da raiz) — só existe em
     * pré-molares e molares (2 ou 3 raízes); ausente em canino/incisivos (raiz única). */
    sliver: number | null
    /** Vista oclusal (o "quadrado" abaixo/acima do dente). */
    face: number
}

// Mapeamento derivado empiricamente de dentes.svg: cada posição (quadrante superior/inferior
// direito) foi identificada pela posição (x,y) de cada <path> depois de aplicado o transform
// do arquivo original — ver generate-odonto-assets.mjs. Conferido visualmente contra o print de
// referência (raiz tripla em 16-18, dupla em 46-48, "X" na face de canino/incisivos).
const UPPER_RIGHT: Record<string, ToothAssetRef> = {
    '18': { tooth: 11, sliver: 12, face: 13 },
    '17': { tooth: 9, sliver: 10, face: 14 },
    '16': { tooth: 7, sliver: 8, face: 15 },
    '15': { tooth: 5, sliver: 6, face: 16 },
    '14': { tooth: 3, sliver: 4, face: 17 },
    '13': { tooth: 0, sliver: null, face: 18 },
    '12': { tooth: 1, sliver: null, face: 19 },
    '11': { tooth: 2, sliver: null, face: 20 },
}
const LOWER_RIGHT: Record<string, ToothAssetRef> = {
    '48': { tooth: 37, sliver: 38, face: 21 },
    '47': { tooth: 35, sliver: 36, face: 22 },
    '46': { tooth: 29, sliver: 30, face: 23 },
    '45': { tooth: 31, sliver: 32, face: 24 },
    '44': { tooth: 33, sliver: 34, face: 25 },
    '43': { tooth: 39, sliver: null, face: 26 },
    '42': { tooth: 40, sliver: null, face: 27 },
    '41': { tooth: 41, sliver: null, face: 28 },
}

export interface ToothCell {
    fdi: string
    ref: ToothAssetRef
    /** Lado esquerdo do paciente (quadrantes 2 e 3) — reaproveita o traçado do lado direito
     * espelhado horizontalmente (CSS `scaleX(-1)`), não são traçados próprios no arquivo fonte. */
    mirrored: boolean
}

const UPPER_RIGHT_ORDER = ['18', '17', '16', '15', '14', '13', '12', '11']
const UPPER_LEFT_ORDER = ['21', '22', '23', '24', '25', '26', '27', '28']
const LOWER_RIGHT_ORDER = ['48', '47', '46', '45', '44', '43', '42', '41']
const LOWER_LEFT_ORDER = ['31', '32', '33', '34', '35', '36', '37', '38']

// 21 espelha 11 (incisivo central, junto à linha média), 28 espelha 18 (terceiro molar, na
// extremidade) — ou seja, a ORDEM da fonte espelhada é a direita LIDA DE TRÁS PRA FRENTE, não a
// mesma ordem. Erro anterior pareava 21↔18 (invertido) por reusar o mesmo índice `i`.
const UPPER_RIGHT_REVERSED = [...UPPER_RIGHT_ORDER].reverse() // ['11','12',...,'18']
const LOWER_RIGHT_REVERSED = [...LOWER_RIGHT_ORDER].reverse() // ['41','42',...,'48']

/** 16 posições, esquerda→direita: 18..11, 21..28. */
export const UPPER_ARCH: ToothCell[] = [
    ...UPPER_RIGHT_ORDER.map((fdi): ToothCell => ({ fdi, ref: UPPER_RIGHT[fdi], mirrored: false })),
    ...UPPER_LEFT_ORDER.map((fdi, i): ToothCell => ({ fdi, ref: UPPER_RIGHT[UPPER_RIGHT_REVERSED[i]], mirrored: true })),
]

/** 16 posições, esquerda→direita: 48..41, 31..38. */
export const LOWER_ARCH: ToothCell[] = [
    ...LOWER_RIGHT_ORDER.map((fdi): ToothCell => ({ fdi, ref: LOWER_RIGHT[fdi], mirrored: false })),
    ...LOWER_LEFT_ORDER.map((fdi, i): ToothCell => ({ fdi, ref: LOWER_RIGHT[LOWER_RIGHT_REVERSED[i]], mirrored: true })),
]

// Decíduos: só 5 posições por quadrante (sem pré-molares nem 3º molar) — reaproveita as MESMAS
// formas dos permanentes (incisivo central/lateral, canino, e as duas formas de molar), sem
// nenhum traçado próprio no arquivo fonte. Pares [fdi decíduo, fdi permanente cuja forma é
// reaproveitada], já na ordem de tela esquerda→direita (mesma lógica de UPPER_RIGHT_ORDER acima:
// quadrante direito do paciente aparece à esquerda da tela, longe→perto da linha média; quadrante
// esquerdo aparece à direita, perto→longe).
const UPPER_RIGHT_DECID: [string, string][] = [
    ['55', '17'],
    ['54', '16'],
    ['53', '13'],
    ['52', '12'],
    ['51', '11'],
]
const UPPER_LEFT_DECID: [string, string][] = [
    ['61', '11'],
    ['62', '12'],
    ['63', '13'],
    ['64', '16'],
    ['65', '17'],
]
const LOWER_RIGHT_DECID: [string, string][] = [
    ['85', '47'],
    ['84', '46'],
    ['83', '43'],
    ['82', '42'],
    ['81', '41'],
]
const LOWER_LEFT_DECID: [string, string][] = [
    ['71', '41'],
    ['72', '42'],
    ['73', '43'],
    ['74', '46'],
    ['75', '47'],
]

/** 10 posições, esquerda→direita: 55..51, 61..65. */
export const UPPER_ARCH_DECID: ToothCell[] = [
    ...UPPER_RIGHT_DECID.map(([fdi, shapeKey]): ToothCell => ({ fdi, ref: UPPER_RIGHT[shapeKey], mirrored: false })),
    ...UPPER_LEFT_DECID.map(([fdi, shapeKey]): ToothCell => ({ fdi, ref: UPPER_RIGHT[shapeKey], mirrored: true })),
]

/** 10 posições, esquerda→direita: 85..81, 71..75. */
export const LOWER_ARCH_DECID: ToothCell[] = [
    ...LOWER_RIGHT_DECID.map(([fdi, shapeKey]): ToothCell => ({ fdi, ref: LOWER_RIGHT[shapeKey], mirrored: false })),
    ...LOWER_LEFT_DECID.map(([fdi, shapeKey]): ToothCell => ({ fdi, ref: LOWER_RIGHT[shapeKey], mirrored: true })),
]

export { ODONTO_PATHS }
